import {
	auditService,
	casesService,
	GET_CASE_ID,
	GET_CASE_STEPS_STATUS,
	GET_LANDING_PAGE_CASE_DETAILS,
	GET_LAWYER_CASE_DETAILS,
} from '@/api';
import { caseApplicantService } from '@/api/caseApplicant';
import { ButtonsForm } from '@/components/dashboard';
import { InputDate } from '@/components/form';
import { CaseDetailsLandingScreen } from '@/components/screens/cases';
import { autosaveTimingApplicantIntake, errorFieldMessage } from '@/const';
import { CaseContext } from '@/context/CaseContext';
import { useNotification } from '@/hooks';
import useModalPreviewPage from '@/hooks/useModalPreviewPage';
import {
	ApprovePhase,
	CaseDetailsLawyer,
	Category,
	LandingPageCaseDetailsPayload,
	StepStatusCase,
} from '@/interfaces';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { useRouter } from 'next/router';
import * as React from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useDebouncedCallback } from 'use-debounce';
import { SearchImage } from './SearchImage';
import { UploadImage } from './UploadImage';
import { ErrorMessageInput } from '@/components/common';
import { RichEditor } from '@/components/common/rich-editor/RichEditor';
import { formatUTCToLocal, isRichTextEmpty } from '@/lib';
import { getValidCaseId } from '@/utils';

interface LandingPageCaseDetailsI {
	caseCategory?: Category;
	phaseId: number;
	stepCase?: StepStatusCase;
	caseDetails: CaseDetailsLawyer | undefined;
}

export const LandingPageCaseDetails: React.FC<LandingPageCaseDetailsI> = ({
	caseCategory,
	phaseId,
	stepCase,
	caseDetails,
}) => {
	const router = useRouter();
	const caseId = getValidCaseId(router.query.id);
	const id = caseId;
	const queryClient = useQueryClient();
	const [isLoadingAutosaved, setIsLoadingAutosaved] = React.useState(false);
	const { isCaseReadOnly, setSaveStatus } = React.useContext(CaseContext);
	const isStepCompleted = stepCase?.status === 'completed';
	const isStepApproved = stepCase?.status === 'approved';

	const { toastError } = useNotification();
	const [file, setFile] = React.useState<File | null>(null);
	const [srcImg, setSrcImg] = React.useState<string | null>(null);
	const [sourceType, setSourceType] = React.useState<string>('');
	const [isFilled, setIsFilled] = React.useState<boolean>(false);
	const [isDebouncing, setIsDebouncing] = React.useState(false);
	const [showErrorFile, setShowErrorFile] = React.useState<boolean>(false);

	const { ModalPreviewPage, hidePreviewPage, showPreviewPage } =
		useModalPreviewPage();

	// form
	const {
		register,
		setValue,
		getValues,
		control,
		watch,
		formState: { errors },
		handleSubmit,
		clearErrors,
	} = useForm({
		mode: 'onSubmit',
	});

	const aboutCompany = watch('about_company');
	const aboutClaim = watch('about_claim');
	const eligibilityCriteria = watch('eligibility_criteria');

	// Observa todos los campos del formulario
	const formValues = useWatch({
		control,
	});

	const rules = {
		about_company: {
			required: { value: true, message: errorFieldMessage },
		},
		about_claim: {
			required: { value: true, message: errorFieldMessage },
		},
		eligibility_criteria: {
			required: { value: true, message: errorFieldMessage },
		},
		picture: {
			required: { value: true, message: errorFieldMessage },
		},
	};

	const { data: landingPageCaseDetails, refetch } = useQuery({
		queryKey: [GET_LANDING_PAGE_CASE_DETAILS, id],
		queryFn: () => caseApplicantService.getLandingPageCaseDetails(id as string),
		enabled: !!id,
	});

	const { data: currentCaseRes, refetch: refetchCaseRes } = useQuery({
		queryKey: [GET_CASE_ID, id],
		queryFn: () => casesService.getById(id as string),
		enabled: !!id,
		refetchOnWindowFocus: false,
	});

	React.useEffect(() => {
		if (landingPageCaseDetails) {
			setValue('about_company', landingPageCaseDetails.data.about_company, {
				shouldDirty: true,
			});
			setValue('about_claim', landingPageCaseDetails.data.about_claim, {
				shouldDirty: true,
			});
			setValue('file', landingPageCaseDetails.data.cover_image, {
				shouldDirty: true,
			});
			setValue(
				'eligibility_criteria',
				landingPageCaseDetails.data.eligibility_criteria,
				{
					shouldDirty: true,
				}
			);
			setValue(
				'registration_deadline',
				landingPageCaseDetails.data.registration_deadline
					? format(
							parseISO(landingPageCaseDetails.data.registration_deadline),
							'MM-dd-yyyy'
					  )
					: null,
				{
					shouldDirty: true,
				}
			);
			setSourceType(landingPageCaseDetails.data.image_source_type);
			setSrcImg(landingPageCaseDetails.data.cover_image);
		}
	}, [landingPageCaseDetails]);

	const isFormFilled = React.useMemo(() => {
		const areAllFieldsFilled = Object.entries(formValues).every(
			([key, value]) => {
				if (caseCategory !== 'Securities' && key === 'registration_deadline') {
					return true; // Allow null or empty value for this field
				}
				return value !== '' && value !== undefined && value !== null;
			}
		);

		return areAllFieldsFilled && !!srcImg;
	}, [formValues, caseCategory, srcImg]);

	React.useEffect(() => {
		if (!caseDetails) return;
		const areAllFieldsFilled = Object.entries(formValues).every(
			([key, value]) => {
				if (caseCategory !== 'Securities' && key === 'registration_deadline') {
					return true;
				}
				if (value === undefined || value === null) return false;

				if (typeof value === 'string') {
					if (value.trim() === '') return false;
					if (isRichTextEmpty(value)) return false;
				}

				return true;
			}
		);

		const isFormFilled = areAllFieldsFilled && !!srcImg;
		setIsFilled(isFormFilled);
		clearErrors();
		setShowErrorFile(false);

		if (getInfoIsEdited()) {
			if (isFormFilled) {
				debouncedHandle('complete');
			}

			if (!isFormFilled && caseDetails?.is_draft) {
				debouncedHandle('save');
			}
		}
	}, [formValues, caseCategory, srcImg]);

	const debouncedHandle = useDebouncedCallback((type: 'save' | 'complete') => {
		setIsDebouncing(true);
		if (type === 'complete') {
			handleComplete();
		} else {
			handleSave();
		}
	}, autosaveTimingApplicantIntake);

	const handleSetFile = (url: string | null, file: File | null): void => {
		setValue('file', url);
		setSrcImg(url);
		setFile(file);
		setSourceType('file');
	};

	const getInfoIsEdited = () => {
		const formHasValues = Object.entries(formValues).some(([key, value]) => {
			return key === 'option' ? false : value;
		});

		if (landingPageCaseDetails && formHasValues) {
			const dataSaved = landingPageCaseDetails.data;
			if (
				dataSaved.about_claim !== formValues.about_claim ||
				dataSaved.about_company !== formValues.about_company ||
				dataSaved.eligibility_criteria !== formValues.eligibility_criteria ||
				dataSaved.cover_image !== srcImg ||
				format(dataSaved.registration_deadline, 'yyy-MM-dd') !==
					format(formValues.registration_deadline, 'yyy-MM-dd')
			) {
				return true;
			}
		}
		return false;
	};

	const getDataToSave = () => {
		const data = getValues();
		const newData: LandingPageCaseDetailsPayload = {
			about_claim: data.about_claim || null,
			about_company: data.about_company || null,
			eligibility_criteria: data.eligibility_criteria || null,
			image_source_type: sourceType,
		};
		if (data.registration_deadline) {
			newData['registration_deadline'] = `${format(
				data.registration_deadline,
				'yyyy-MM-dd'
			)}T00:00:00Z`;
		}
		if (file) {
			newData['cover_image'] = file;
		}
		if (data.cover_image_url) {
			newData['cover_image_url'] = data.cover_image_url;
		}

		return newData;
	};

	const handleSave = async () => {
		try {
			clearErrors();
			const newData = getDataToSave();
			if (srcImg === null) {
				await caseApplicantService.patchImageLandingPageCaseDetails(
					id as string
				);
			}
			await caseApplicantService.postLandingPageCaseDetails(
				id as string,
				newData
			);
			refetch();
			refetchCaseRes();
			if (caseId) {
				queryClient.refetchQueries({
					queryKey: [GET_CASE_STEPS_STATUS, caseId],
				});
			}
			// debouncedToast();
		} catch (error) {
			toastError('An error occurred while saving. Please try again.');
		} finally {
			setIsDebouncing(false);
		}
	};

	const handleComplete = async () => {
		if (!isFilled) return;
		setIsLoadingAutosaved(true);
		setSaveStatus({ isError: false });

		try {
			const newData = getDataToSave();
			await caseApplicantService.putLandingPageCaseDetails(
				id as string,
				newData
			);
			setSaveStatus({ isError: true });
			handleApprove();
		} catch (error) {
			toastError(
				'An error occurred while confirming completion. Please try again.'
			);
			setSaveStatus({ isError: true });
		} finally {
			setIsDebouncing(false);
			setIsLoadingAutosaved(false);
		}
	};

	const handleApprove = async () => {
		setSaveStatus({ isError: false });
		try {
			if (landingPageCaseDetails && stepCase) {
				const data: ApprovePhase = {
					case_id: landingPageCaseDetails?.data.id,
					lawyer_stepper_id: phaseId,
					stepper_form_id: stepCase?.step_id,
				};
				await auditService.postApprovePhase(data);
				hidePreviewPage();
				refetch();
				refetchCaseRes();
				if (caseId) {
					queryClient.refetchQueries({
						queryKey: [GET_CASE_STEPS_STATUS, caseId],
					});
					queryClient.refetchQueries({
						queryKey: [GET_LAWYER_CASE_DETAILS, caseId],
					});
				}
				setSaveStatus({ isError: false });
			}
		} catch (error) {
			toastError('An error has occurred while approving. Please try again.');
			setSaveStatus({ isError: true });
		}
	};

	const handlePreview = () => {
		if ((!isStepCompleted || !isFormFilled || isDebouncing) && !isStepApproved)
			return;

		showPreviewPage();
	};

	React.useEffect(() => {
		setSaveStatus({ isSaving: isLoadingAutosaved });
	}, [isLoadingAutosaved]);

	return (
		<div className="w-full">
			<form className="flex flex-col gap-y-6">
				<RichEditor
					disabled={isCaseReadOnly}
					name="eligibility_criteria"
					title="Eligibility Criteria"
					register={register}
					classNameInput="font-normal text-opacity-black resize-y min-h-[120px] scroll-custom2"
					classNameTitle="font-normal"
					fontSize="lg"
					value={eligibilityCriteria}
					rules={rules.eligibility_criteria}
					error={errors.eligibility_criteria}
					onChange={(value) => setValue('eligibility_criteria', value)}
				/>

				<RichEditor
					disabled={isCaseReadOnly}
					name="about_claim"
					title="About the Claim"
					register={register}
					classNameInput="font-normal text-opacity-black resize-y min-h-[120px] scroll-custom2"
					classNameTitle="font-normal"
					fontSize="lg"
					value={aboutClaim}
					rules={rules.about_claim}
					error={errors.about_claim}
					onChange={(value) => setValue('about_claim', value)}
				/>

				<RichEditor
					title="About the Company"
					name="about_company"
					classNameInput="font-normal text-opacity-black resize-y min-h-[120px] scroll-custom2"
					fontSize="lg"
					disabled={isCaseReadOnly}
					register={register}
					rules={rules.about_claim}
					error={errors.about_claim}
					value={aboutCompany}
					onChange={(value) => setValue('about_company', value)}
				/>

				{caseCategory === 'Securities' && (
					<InputDate
						disabled={isCaseReadOnly}
						title="Registration Deadline"
						name="registration_deadline"
						register={register}
						setValueInput={setValue}
						dateDefault={
							landingPageCaseDetails &&
							landingPageCaseDetails.data.registration_deadline
								? formatUTCToLocal(
										landingPageCaseDetails?.data.registration_deadline
								  )
								: ''
						}
					/>
				)}
				<div className="mt-2">
					<div className="flex items-start gap-6">
						<div className="w-2/5">
							<UploadImage
								file={file}
								srcImg={srcImg}
								setFile={setFile}
								register={register}
								setValue={setValue}
								setSrcImg={setSrcImg}
								sourceType={sourceType}
								setSourceType={setSourceType}
								handleSetFile={handleSetFile}
								isCaseReadOnly={isCaseReadOnly}
							/>
						</div>

						<div className="w-3/5">
							<SearchImage
								srcImg={srcImg}
								setFile={setFile}
								setSrcImg={setSrcImg}
								setValue={setValue}
								sourceType={sourceType}
								setSourceType={setSourceType}
								isCaseReadOnly={isCaseReadOnly}
							/>
						</div>
					</div>
					{showErrorFile && !file && !srcImg && (
						<ErrorMessageInput message={errorFieldMessage} />
					)}
				</div>

				<ButtonsForm
					onClickPreview={() => {
						if (isStepApproved) {
							handlePreview();
						} else {
							handleSubmit(handlePreview)();
							setShowErrorFile(true);
						}
					}}
					completeButtonClassName={
						(!isStepCompleted || !isFormFilled || isDebouncing) &&
						!isStepApproved
							? 'opacity-60 cursor-default'
							: ''
					}
					onlyOneButton
					isApproved={isStepApproved}
					buttonTitle={'Preview Landing Page'}
				/>
			</form>
			<ModalPreviewPage
				classNameHeader="md:h-[112px]"
				classNameCardContainer={'!bg-[#F4F4F9]'}
				handleClickApprove={handleApprove}
				handleClickBack={hidePreviewPage}
				handleClickEdit={hidePreviewPage}
				backCustomLabel="Back to edit"
				title={
					isStepApproved || isCaseReadOnly
						? 'This is your preview page.'
						: `This is your preview page. Go back to make changes or 'Approve' to stage it for going live.`
				}
				hideApproveButton={true}
				hideEditButton={true}
			>
				<CaseDetailsLandingScreen
					caseData={currentCaseRes}
					previewMode={true}
				/>
			</ModalPreviewPage>
		</div>
	);
};
